import { type ApiProblem, createApiClient } from '@rental/contracts/client'
import { authBuild } from './auth-build'
import {
  browserDeviceStore,
  createTokenKeeper,
  type TokenKeeper,
  UNAVAILABLE_STORE,
} from './credentials'
import { ProblemError } from './problem-error'
import { isNativeShell } from './shell'

export const APP_VERSION = `staff/${__APP_VERSION__}`

/** A1: cookie (the web app) or token (the iPad app), fixed at build (auth-build.ts). */
const build = authBuild({
  VITE_STAFF_AUTH: import.meta.env.VITE_STAFF_AUTH,
  VITE_API_ORIGIN: import.meta.env.VITE_API_ORIGIN,
})
export const AUTH_MODE = build.mode

/**
 * Where the API answers. The web app: its own origin, where `/v1` is the API
 * and the httpOnly cookies travel with every call and nowhere else. The iPad
 * app: the API's own site, with the credentials in headers.
 */
export const API_ORIGIN = build.mode === 'token' ? build.apiOrigin : window.location.origin

/**
 * The iPad app's credentials (credentials.ts); null on the web. Inside the
 * native shell the device credential lives in the Keychain (A2), never in the
 * page's storage: until the shell supplies it, the store is unavailable.
 */
export const keeper: TokenKeeper | null =
  build.mode === 'token'
    ? createTokenKeeper({
        apiOrigin: build.apiOrigin,
        store: isNativeShell() ? UNAVAILABLE_STORE : browserDeviceStore(() => window.localStorage),
      })
    : null

/** Every call to the API, its uploads and its files: through the keeper in the app. */
export const apiFetch: (request: Request) => Promise<Response> = keeper
  ? keeper.fetch
  : (request) => fetch(request)

/** The generated client (CLAUDE.md: front-ends use only this). */
export const api = createApiClient({
  baseUrl: API_ORIGIN,
  appVersion: APP_VERSION,
  credentials: keeper ? 'omit' : 'same-origin',
  ...(keeper ? { fetch: keeper.fetch } : {}),
})

export { ProblemError } from './problem-error'

/** For queries: the data, or a thrown ProblemError. */
export async function load<T>(
  result: Promise<{ ok: true; data: T } | { ok: false; problem: ApiProblem }>,
): Promise<T> {
  const settled = await result
  if (!settled.ok) throw new ProblemError(settled.problem)
  return settled.data
}
